import chalk, { type ChalkInstance } from 'chalk'

import { Logger, LoggerOptions, LogLevel } from './logger.types'
import { getDefaultLogLevel } from './logger.utils'

export class DefaultLogger implements Logger {
  private readonly levelPriority: Record<LogLevel, number> = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3,
  }

  private readonly levelColors: Record<LogLevel, ChalkInstance> = {
    debug: chalk.magenta,
    info: chalk.blue,
    warn: chalk.yellow,
    error: chalk.red,
  }

  private currentLevel: LogLevel
  private timestamp: boolean

  constructor(options?: LoggerOptions) {
    // An explicit level always wins; otherwise fall back to the env-based
    // default (info in development, error in production).
    this.currentLevel = options?.level ?? getDefaultLogLevel()
    this.timestamp = options?.timestamp ?? true
  }

  private shouldLog(level: LogLevel) {
    return this.levelPriority[level] >= this.levelPriority[this.currentLevel]
  }

  private format(level: LogLevel, message: string, context?: string) {
    const ts = this.timestamp ? `${new Date().toISOString()}` : ''
    const ctx = context ? chalk.cyan(`[${context}]`) : ''
    const paddedLevel = this.levelColors[level](level.toUpperCase().padStart(5, ' '))
    const sdkPrefix = chalk.green('[MarzbanSDK]')

    return `${sdkPrefix} ${ts} ${paddedLevel} ${ctx} ${message}`.trim()
  }

  debug(message: string, context?: string) {
    if (this.shouldLog('debug')) {
      console.debug(this.format('debug', message, context))
    }
  }

  info(message: string, context?: string) {
    if (this.shouldLog('info')) {
      console.info(this.format('info', message, context))
    }
  }

  warn(message: string, context?: string) {
    if (this.shouldLog('warn')) {
      console.warn(this.format('warn', message, context))
    }
  }

  error(message: string, trace?: unknown, context?: string) {
    if (this.shouldLog('error')) {
      console.error(this.format('error', message, context), trace ?? '')
    }
  }
}
